import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  Award,
  Boxes,
  ClipboardList,
  Plus,
  Save,
  ThumbsDown,
  ThumbsUp,
  Trash2,
} from 'lucide-react';
import {
  Badge,
  Button,
  Card,
  CardBody,
  EmptyState,
  Input,
  PageHeader,
  Select,
  Spinner,
  Textarea,
  useToast,
  type SelectOption,
} from '../../ui';
import { useActiveRoom } from '../../features/rooms/RoomContext';
import { useSprints } from '../../features/sprintboard/useSprints';
import { usePeopleDirectory } from '../../features/teams/usePeopleDirectory';
import { useSprintRetro } from '../../features/sprintboard/retro/useSprintRetro';
import {
  RETRO_ACTION_ITEM_STATUSES,
  addActionItem,
  addDidntGoWell,
  addKudos,
  addWentWell,
  blankRetroDraft,
  countsBySection,
  draftFromRetro,
  removeActionItem,
  removeDidntGoWell,
  removeKudos,
  removeWentWell,
  updateActionItem,
  updateDidntGoWell,
  updateKudos,
  updateWentWell,
  validateRetroDraft,
} from '../../features/sprintboard/retro/sprintRetroModel';
import type {
  RetroActionItemStatus,
  SprintRetroDraft,
} from '../../features/sprintboard/retro/types';
import type { Sprint } from '../../features/sprintboard/models/types';

/** Human labels for the action-item statuses. */
const STATUS_LABEL: Record<RetroActionItemStatus, string> = {
  open: 'Open',
  in_progress: 'In progress',
  done: 'Done',
};

const STATUS_OPTIONS: SelectOption[] = RETRO_ACTION_ITEM_STATUSES.map((s) => ({
  value: s,
  label: STATUS_LABEL[s],
}));

/** Picks a sensible default sprint: the most recent COMPLETED one, else the first. */
function defaultSprint(sprints: Sprint[]): Sprint | null {
  if (sprints.length === 0) return null;
  return sprints.find((s) => s.status === 'complete') ?? sprints[0];
}

/**
 * An owner picker driven by the team people directory. Renders a Select of
 * aliases when a directory exists, else falls back to a free-text alias input.
 */
function OwnerPicker({
  value,
  people,
  onChange,
  label,
}: {
  value: string;
  people: SelectOption[];
  onChange: (owner: string) => void;
  label?: string;
}) {
  if (people.length > 1) {
    return (
      <Select
        label={label}
        value={value}
        options={people}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }
  return (
    <Input
      label={label}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder="owner alias"
    />
  );
}

/**
 * RetroPage — a per-sprint retrospective. Pick a sprint (defaults to the most
 * recent COMPLETED sprint), then edit three regions: **What went well** (text
 * lines), **What didn't go well** (items that each carry multiple action items,
 * every action with an owner from the team directory + a status), and **Kudos**
 * (to + message, from = current user by default). Save persists the whole retro
 * as one document (upsert keyed by sprintId).
 *
 * Reads/writes exclusively through {@link useSprintRetro} (hook → store → api
 * facade) + {@link useSprints} + {@link usePeopleDirectory}; it never touches
 * the network directly. Tenant + room scoped automatically.
 */
export function RetroPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { activeRoom, activeRoomId } = useActiveRoom();
  const { sprints, loading: sprintsLoading } = useSprints();
  const { people } = usePeopleDirectory();
  const {
    retros,
    currentAlias,
    loading: retroLoading,
    error,
    loadForSprint,
    save,
    remove,
  } = useSprintRetro();

  const [sprintId, setSprintId] = useState<string>('');
  const [draft, setDraft] = useState<SprintRetroDraft>(() =>
    blankRetroDraft(''),
  );
  const [loadingRetro, setLoadingRetro] = useState(false);
  const [saving, setSaving] = useState(false);
  const [problems, setProblems] = useState<string[]>([]);

  const sprintById = useMemo(
    () => new Map(sprints.map((s) => [s.id, s])),
    [sprints],
  );
  const selectedSprint = sprintId ? sprintById.get(sprintId) : undefined;

  const sprintsWithRetro = useMemo(
    () => new Set(retros.map((r) => r.sprintId)),
    [retros],
  );

  // Default the picker to the most recent completed sprint once loaded.
  useEffect(() => {
    if (!sprintId && sprints.length > 0) {
      const def = defaultSprint(sprints);
      if (def) setSprintId(def.id);
    }
  }, [sprints, sprintId]);

  // Hydrate the selected sprint's retro (or start a blank draft).
  const hydrate = useCallback(
    async (id: string) => {
      if (!id) {
        setDraft(blankRetroDraft(''));
        return;
      }
      setLoadingRetro(true);
      setProblems([]);
      try {
        const existing = await loadForSprint(id);
        const name = sprintById.get(id)?.name;
        setDraft(
          existing
            ? draftFromRetro(existing)
            : blankRetroDraft(id, name),
        );
      } catch (e) {
        toast({
          tone: 'error',
          title: 'Failed to load retro',
          description: e instanceof Error ? e.message : 'Unknown error',
        });
        setDraft(blankRetroDraft(id, sprintById.get(id)?.name));
      } finally {
        setLoadingRetro(false);
      }
    },
    [loadForSprint, sprintById, toast],
  );

  useEffect(() => {
    if (sprintId) void hydrate(sprintId);
  }, [sprintId, hydrate]);

  const sprintOptions = useMemo<SelectOption[]>(
    () =>
      sprints.map((s) => ({
        value: s.id,
        label: `${s.name}${s.status === 'complete' ? ' — completed' : ''}${
          sprintsWithRetro.has(s.id) ? ' ✓' : ''
        }`,
      })),
    [sprints, sprintsWithRetro],
  );

  const peopleOptions = useMemo<SelectOption[]>(
    () => [
      { value: '', label: 'Unassigned' },
      ...people.map((p) => ({
        value: p.alias,
        label: p.name ? `${p.name} (${p.alias})` : p.alias,
      })),
    ],
    [people],
  );

  const counts = useMemo(() => countsBySection(draft), [draft]);

  const onSave = async () => {
    const found = validateRetroDraft(draft);
    if (found.length > 0) {
      setProblems(found);
      return;
    }
    setProblems([]);
    setSaving(true);
    try {
      await save({ ...draft, sprintName: selectedSprint?.name });
      toast({ tone: 'success', title: 'Retro saved' });
    } catch (e) {
      toast({
        tone: 'error',
        title: 'Save failed',
        description: e instanceof Error ? e.message : 'Unknown error',
      });
    } finally {
      setSaving(false);
    }
  };

  const onClear = async () => {
    if (!sprintId) return;
    setSaving(true);
    try {
      await remove(sprintId);
      setDraft(blankRetroDraft(sprintId, selectedSprint?.name));
      toast({ tone: 'success', title: 'Retro cleared' });
    } catch (e) {
      toast({
        tone: 'error',
        title: 'Clear failed',
        description: e instanceof Error ? e.message : 'Unknown error',
      });
    } finally {
      setSaving(false);
    }
  };

  if (!activeRoomId) {
    return (
      <div>
        <PageHeader
          icon={<ClipboardList className="h-5 w-5" />}
          title="Sprint Retro"
          description="Per-sprint retrospectives: what went well, what didn't, and kudos."
        />
        <EmptyState
          icon={<Boxes className="h-6 w-6" />}
          title="No active room"
          description="Retros are scoped to a room's sprints. Select or create a room first."
          action={<Button onClick={() => navigate('/rooms')}>Go to Rooms</Button>}
        />
      </div>
    );
  }

  const loading = sprintsLoading || retroLoading;

  return (
    <div>
      <PageHeader
        icon={<ClipboardList className="h-5 w-5" />}
        title="Sprint Retro"
        description={
          <span className="inline-flex items-center gap-1.5">
            Retrospective for
            <Badge tone="accent">{activeRoom?.name ?? 'active room'}</Badge>
          </span>
        }
        actions={
          selectedSprint ? (
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                leftIcon={<Trash2 className="h-3.5 w-3.5" />}
                onClick={() => void onClear()}
                disabled={saving}
              >
                Clear
              </Button>
              <Button
                size="sm"
                leftIcon={<Save className="h-3.5 w-3.5" />}
                onClick={() => void onSave()}
                loading={saving}
              >
                Save retro
              </Button>
            </div>
          ) : undefined
        }
      />

      {loading ? (
        <div className="flex items-center justify-center py-24">
          <Spinner label="Loading retro" />
        </div>
      ) : error ? (
        <EmptyState
          icon={<AlertTriangle className="h-6 w-6" />}
          title="Couldn't load retros"
          description={error}
        />
      ) : sprints.length === 0 ? (
        <EmptyState
          icon={<ClipboardList className="h-6 w-6" />}
          title="No sprints yet"
          description="Create a sprint in this room first — a retro is written against a sprint."
          action={<Button onClick={() => navigate('/work/sprints')}>Go to Sprints</Button>}
        />
      ) : (
        <div className="space-y-5">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-[minmax(0,24rem)_1fr] sm:items-end">
            <Select
              label="Sprint"
              value={sprintId}
              options={sprintOptions}
              onChange={(e) => setSprintId(e.target.value)}
            />
            {selectedSprint && (
              <div className="flex flex-wrap items-center gap-1.5 pb-1.5">
                <Badge tone={selectedSprint.status === 'complete' ? 'success' : 'info'}>
                  {selectedSprint.status === 'complete' ? 'Completed' : selectedSprint.status}
                </Badge>
                <span className="text-xs text-zinc-500">
                  {counts.wentWell} went-well · {counts.didntGoWell} issues ·{' '}
                  {counts.openActionItems}/{counts.actionItems} open actions ·{' '}
                  {counts.kudos} kudos
                </span>
              </div>
            )}
          </div>

          {problems.length > 0 && (
            <ul
              className="space-y-1 rounded-xl border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-300"
              role="alert"
            >
              {problems.map((p) => (
                <li key={p}>{p}</li>
              ))}
            </ul>
          )}

          {loadingRetro ? (
            <div className="flex items-center justify-center py-24">
              <Spinner label="Loading sprint retro" />
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
              {/* What went well */}
              <Card>
                <CardBody className="space-y-3">
                  <div className="flex items-center gap-2">
                    <span className="flex h-7 w-7 items-center justify-center rounded-full bg-emerald-500/10 text-emerald-300">
                      <ThumbsUp className="h-4 w-4" aria-hidden="true" />
                    </span>
                    <h3 className="text-sm font-semibold text-zinc-100">
                      What went well
                    </h3>
                  </div>
                  <div className="space-y-2">
                    {draft.wentWell.map((line) => (
                      <div key={line.id} className="flex items-start gap-2">
                        <Textarea
                          value={line.text}
                          rows={2}
                          className="flex-1"
                          placeholder="Something that went well…"
                          onChange={(e) =>
                            setDraft((d) => updateWentWell(d, line.id, e.target.value))
                          }
                        />
                        <Button
                          variant="ghost"
                          size="sm"
                          aria-label="Remove line"
                          onClick={() =>
                            setDraft((d) => removeWentWell(d, line.id))
                          }
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    ))}
                    {draft.wentWell.length === 0 && (
                      <p className="text-xs text-zinc-500">Nothing added yet.</p>
                    )}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    leftIcon={<Plus className="h-3.5 w-3.5" />}
                    onClick={() => setDraft((d) => addWentWell(d))}
                  >
                    Add line
                  </Button>
                </CardBody>
              </Card>

              {/* What didn't go well */}
              <Card>
                <CardBody className="space-y-3">
                  <div className="flex items-center gap-2">
                    <span className="flex h-7 w-7 items-center justify-center rounded-full bg-amber-500/10 text-amber-300">
                      <ThumbsDown className="h-4 w-4" aria-hidden="true" />
                    </span>
                    <h3 className="text-sm font-semibold text-zinc-100">
                      What didn&apos;t go well
                    </h3>
                  </div>
                  <div className="space-y-3">
                    {draft.didntGoWell.map((item) => (
                      <div
                        key={item.id}
                        className="rounded-xl border border-surface-border bg-surface-1 p-2.5 space-y-2"
                      >
                        <div className="flex items-start gap-2">
                          <Textarea
                            value={item.text}
                            rows={2}
                            className="flex-1"
                            placeholder="Something that didn't go well…"
                            onChange={(e) =>
                              setDraft((d) =>
                                updateDidntGoWell(d, item.id, e.target.value),
                              )
                            }
                          />
                          <Button
                            variant="ghost"
                            size="sm"
                            aria-label="Remove item"
                            onClick={() =>
                              setDraft((d) => removeDidntGoWell(d, item.id))
                            }
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>

                        {/* Action items (each with an owner + status) */}
                        <div className="space-y-2 border-l-2 border-surface-border pl-2.5">
                          <p className="text-xs font-medium uppercase tracking-wide text-zinc-500">
                            Action items
                          </p>
                          {item.actionItems.map((action) => (
                            <div
                              key={action.id}
                              className="space-y-1.5 rounded-lg bg-surface-2/60 p-2"
                            >
                              <div className="flex items-start gap-2">
                                <Input
                                  value={action.text}
                                  className="flex-1"
                                  placeholder="Follow-up action…"
                                  onChange={(e) =>
                                    setDraft((d) =>
                                      updateActionItem(d, item.id, action.id, {
                                        text: e.target.value,
                                      }),
                                    )
                                  }
                                />
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  aria-label="Remove action item"
                                  onClick={() =>
                                    setDraft((d) =>
                                      removeActionItem(d, item.id, action.id),
                                    )
                                  }
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                              </div>
                              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                                <OwnerPicker
                                  label="Owner"
                                  value={action.owner}
                                  people={peopleOptions}
                                  onChange={(owner) =>
                                    setDraft((d) =>
                                      updateActionItem(d, item.id, action.id, {
                                        owner,
                                      }),
                                    )
                                  }
                                />
                                <Select
                                  label="Status"
                                  value={action.status}
                                  options={STATUS_OPTIONS}
                                  onChange={(e) =>
                                    setDraft((d) =>
                                      updateActionItem(d, item.id, action.id, {
                                        status: e.target
                                          .value as RetroActionItemStatus,
                                      }),
                                    )
                                  }
                                />
                              </div>
                            </div>
                          ))}
                          <Button
                            variant="ghost"
                            size="sm"
                            leftIcon={<Plus className="h-3.5 w-3.5" />}
                            onClick={() =>
                              setDraft((d) => addActionItem(d, item.id))
                            }
                          >
                            Add action item
                          </Button>
                        </div>
                      </div>
                    ))}
                    {draft.didntGoWell.length === 0 && (
                      <p className="text-xs text-zinc-500">Nothing added yet.</p>
                    )}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    leftIcon={<Plus className="h-3.5 w-3.5" />}
                    onClick={() => setDraft((d) => addDidntGoWell(d))}
                  >
                    Add item
                  </Button>
                </CardBody>
              </Card>

              {/* Kudos */}
              <Card>
                <CardBody className="space-y-3">
                  <div className="flex items-center gap-2">
                    <span className="flex h-7 w-7 items-center justify-center rounded-full bg-accent-soft text-accent-fg">
                      <Award className="h-4 w-4" aria-hidden="true" />
                    </span>
                    <h3 className="text-sm font-semibold text-zinc-100">Kudos</h3>
                  </div>
                  <div className="space-y-3">
                    {draft.kudos.map((k) => (
                      <div
                        key={k.id}
                        className="rounded-xl border border-surface-border bg-surface-1 p-2.5 space-y-2"
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-xs text-zinc-500">
                            from <span className="text-zinc-300">{k.from || currentAlias}</span>
                          </span>
                          <Button
                            variant="ghost"
                            size="sm"
                            aria-label="Remove kudos"
                            onClick={() => setDraft((d) => removeKudos(d, k.id))}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                        <OwnerPicker
                          label="To"
                          value={k.to}
                          people={peopleOptions}
                          onChange={(to) =>
                            setDraft((d) => updateKudos(d, k.id, { to }))
                          }
                        />
                        <Textarea
                          label="Message"
                          value={k.message}
                          rows={2}
                          placeholder="Shout-out message…"
                          onChange={(e) =>
                            setDraft((d) =>
                              updateKudos(d, k.id, { message: e.target.value }),
                            )
                          }
                        />
                      </div>
                    ))}
                    {draft.kudos.length === 0 && (
                      <p className="text-xs text-zinc-500">No kudos yet.</p>
                    )}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    leftIcon={<Plus className="h-3.5 w-3.5" />}
                    onClick={() =>
                      setDraft((d) => addKudos(d, currentAlias))
                    }
                  >
                    Add kudos
                  </Button>
                </CardBody>
              </Card>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default RetroPage;
